import { scenarioEvidence } from "../data/seed";
import type { RoleDefinition } from "../app/roles";
import { useScenario } from "../simulation/ScenarioContext";

type EvidenceDrawerProps = {
  role: RoleDefinition;
};

export function EvidenceDrawer({ role }: EvidenceDrawerProps) {
  const {
    closeDrawer,
    drawerOpen,
    linkedEvidenceIds,
    linkEvidence,
    receipts,
    selectedNode,
    stage,
  } = useScenario();
  const evidence = scenarioEvidence.filter((item) => item.traceNodeId === selectedNode.id);
  const relatedReceipts = receipts.filter(
    (receipt) => receipt.recordId === selectedNode.id || evidence.some((item) => item.id === receipt.recordId),
  );

  if (!drawerOpen) return null;

  return (
    <>
      <button
        className="drawer-scrim is-open"
        onClick={closeDrawer}
        type="button"
      >
        <span className="sr-only">Close record drawer</span>
      </button>
      <aside
        aria-label={`${selectedNode.label} record context`}
        className="record-drawer is-open"
      >
        <header className="record-drawer__header">
          <div>
            <p className="eyebrow">{selectedNode.eyebrow}</p>
            <h2>{selectedNode.label}</h2>
          </div>
          <button aria-label="Close record drawer" className="icon-close" onClick={closeDrawer} type="button">×</button>
        </header>
        <div className="record-drawer__body">
          <section>
            <span className="field-label">Record ID</span>
            <strong className="mono">{selectedNode.id}</strong>
          </section>
          <section>
            <span className="field-label">Operational context</span>
            <p>{selectedNode.operationalDetail}</p>
          </section>
          <section>
            <span className="field-label">Relationship contract</span>
            <p>{selectedNode.architectureDetail}</p>
          </section>
          <section>
            <div className="drawer-section-heading">
              <span className="field-label">Available evidence</span>
              <span>{evidence.length}</span>
            </div>
            {evidence.length ? evidence.map((item) => {
              const linked = linkedEvidenceIds.includes(item.id);
              return (
                <article className="drawer-evidence" key={item.id}>
                  <strong>{item.label}</strong>
                  <span>{item.source} · {item.observedAt} · {item.confidence} confidence</span>
                  <p>{item.supports}</p>
                  <button
                    className="button button--secondary"
                    disabled={linked || stage === "calm"}
                    onClick={() => linkEvidence(item.id, role.label)}
                    type="button"
                  >
                    {linked ? "Linked to INC-2048" : stage === "calm" ? "Start scenario to link" : "Add to incident evidence"}
                  </button>
                </article>
              );
            }) : <p className="empty-copy">This record has context but no standalone evidence item.</p>}
          </section>
          <section>
            <div className="drawer-section-heading">
              <span className="field-label">Related receipts</span>
              <span>{relatedReceipts.length}</span>
            </div>
            {relatedReceipts.length ? (
              <ol className="mini-timeline">
                {relatedReceipts.map((receipt) => (
                  <li key={receipt.id}>
                    <time>{receipt.occurredAt}</time>
                    <span><strong>{receipt.action}</strong>{receipt.outcome}</span>
                  </li>
                ))}
              </ol>
            ) : <p className="empty-copy">No change receipt references this record yet.</p>}
          </section>
        </div>
      </aside>
    </>
  );
}
